import styled from "@emotion/styled";
import { FieldError, useForm } from "react-hook-form";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { passwordValidationFunction } from "@kaltura/mediaspace-shared-ui/form";
import PasswordField from "./../reset-password/PasswordField";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";

export interface SetPasswordFormProps {
    processing: boolean;
    onSubmit: (data: Record<string, unknown>, onValidationError: (message: string) => void) => void;
    setInitialPassword?: boolean;
    ks: string;
    firstName: string;
    lastName: string;
}

const LargeButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(5),
    width: "100%",
}));

/**
 * Set-Password Form Component. also used for reset-password.
 */
export function SetPasswordForm({
    ks,
    firstName,
    lastName,
    processing,
    onSubmit,
    setInitialPassword,
}: SetPasswordFormProps) {
    const {
        register,
        setError,
        handleSubmit,
        trigger,
        watch,
        formState: { errors },
        resetField,
    } = useForm<any>({
        criteriaMode: "all",
        reValidateMode: "onChange",
        mode: "all",
    });

    const config = useContext(ConfigContext);

    const [serverError, setServerError] = useState("");

    const passwordRef = useRef<HTMLInputElement | null>(null);

    // form base name
    const formName = useMemo(() => {
        return setInitialPassword ? "setPassword" : "resetPassword";
    }, [setInitialPassword]);

    // reset ks to it's new value in form
    useEffect(() => {
        resetField(
            `${formName}.ks`,
            {
                defaultValue: ks,
            }
        );
    }, [ks, formName]);

    // password validation
    const passwordValidation = useMemo(() => {
        return passwordValidationFunction(firstName, lastName, config?.passwordValidations);
    }, [firstName, lastName]);

    // used for password validation
    const passwordValue = watch(`${formName}.password`);
    const passwordRepeatValue = watch("passwordRepeat");

    useEffect(() => {
        // validate password when entered
        passwordValue && trigger(`${formName}.password`);
    }, [trigger, passwordValue]);

    // Reset server-side errors on typing
    useEffect(() => setServerError(""), [passwordValue, passwordRepeatValue, setServerError]);

    const doSubmit = useCallback(
        (data: Record<string, unknown>) =>
            onSubmit(
                data,
                (message) => {
                    passwordRef.current?.focus();
                    setServerError(message);
                }
            ),
        [onSubmit, passwordRef, setServerError]
    );

    const passwordFormField = register(
        `${formName}.password`,
        {
            validate: passwordValidation,
        }
    );

    return (
        <form className="kms-ds-set-password-form" onSubmit={handleSubmit(doSubmit)}>
            <div className={"kms-ds-set-password-form-section"}>
                <PasswordField
                    title={(setInitialPassword ? translate("Password") : translate("New Password")) + " *"}
                    {...passwordFormField}
                    ref={(ref: HTMLInputElement) => {
                        passwordFormField.ref(ref);
                        passwordRef.current = ref;
                    }}
                    error={(errors[formName] as any)?.password || (serverError && { type: "server" })}
                    showErrors={(passwordValue || (errors[formName] as any)?.password) && !serverError}
                    autoComplete="new-password"
                />

                <PasswordField
                    title={
                        (setInitialPassword ? translate("Repeat Password") : translate("Repeat New Password")) + " *"
                    }
                    {...register(
                        "passwordRepeat",
                        {
                            required: true,
                            validate: {
                                matchFirstPassword: (value) =>
                                    value === watch(`${formName}.password`) || translate("Passwords do not match"),
                            },
                        }
                    )}
                    errorMessage={
                        (serverError || (errors.passwordRepeat as FieldError)?.types?.matchFirstPassword) as string
                    }
                    showErrors={passwordRepeatValue && (!!serverError || !!errors.passwordRepeat)}
                    autoComplete="new-password"
                />

                <input className="hidden" type={"hidden"} {...register(`${formName}.ks`)} value={ks} readOnly={true} />
            </div>
            <div className={"kms-ds-set-password-form-buttons-container"}>
                <LargeButton
                    size={"large"}
                    loading={processing}
                    disabled={processing}
                    className={`kms-ds-set-password-form-submit-button`}
                    type="submit"
                >
                    {setInitialPassword ? translate("Set Password") : translate("Update Password")}
                </LargeButton>
            </div>
        </form>
    );
}

export default SetPasswordForm;
